import type { BlockMetadataIndicator } from 'types/builderBlockMetadata';

/** Maps `add_indicator` API payload to `get_block_metadata` indicator shape for local Redux merge. */
export default function mapAddIndicatorResponseToBlockMetadata(
  raw: Record<string, any>,
): BlockMetadataIndicator {
  const formatKey = raw.data_format ?? raw.format ?? 'number';
  const format = typeof formatKey === 'string' ? formatKey.toLowerCase() : 'number';
  const formatProperties = raw.data_format_properties ?? raw.format_properties ?? {};

  const indicatorTypeSource = raw.indicator_type ?? raw.type ?? 'input';
  const indicatorType =
    typeof indicatorTypeSource === 'string' ? indicatorTypeSource.toLowerCase() : 'input';

  const calculated = raw.calculated_formula ?? raw.calculate_formula;
  const calculatedFormula =
    calculated && typeof calculated === 'object' && !Array.isArray(calculated) ? calculated : null;

  return {
    id: raw.id,
    name: raw.name ?? '',
    help: raw.help ?? '',
    type: format,
    format,
    format_properties: {
      scale: formatProperties.scale ?? null,
      measure: formatProperties.measure ?? '',
      null_value: formatProperties.null_value ?? '',
      decimal_points: formatProperties.decimal_points ?? 2,
      thousand_separators: formatProperties.thousand_separators ?? true,
      negative_parentheses: formatProperties.negative_parentheses ?? false,
    },
    output_format: raw.output_format ?? 'default',
    formula: raw.formula ?? null,
    calculated_formula: calculatedFormula,
    data_type: raw.data_type ?? 'Number',
    indicator_type: indicatorType,
    chart_type: raw.chart_type ?? 'line',
    is_default_KPI: Boolean(raw.is_default_KPI),
    is_favourite: raw.favourite !== undefined ? Boolean(raw.favourite) : Boolean(raw.is_favourite),
    warning: raw.warning,
  };
}

/**
 * Applies a PATCH indicator API response onto an existing metadata row (handles partial payloads).
 */
export function mergeIndicatorPatchResponseIntoBlockMetadata(
  previous: BlockMetadataIndicator,
  patchResponse: Record<string, any>,
): BlockMetadataIndicator {
  const raw: Record<string, any> = {
    id: previous.id,
    name: previous.name,
    help: previous.help,
    data_format: previous.format,
    format_properties: previous.format_properties,
    output_format: previous.output_format,
    formula: previous.formula,
    calculated_formula: previous.calculated_formula,
    data_type: previous.data_type,
    indicator_type: previous.indicator_type,
    chart_type: previous.chart_type,
    is_default_KPI: previous.is_default_KPI,
    favourite: previous.is_favourite,
    is_favourite: previous.is_favourite,
    warning: previous.warning,
    ...patchResponse,
  };
  return mapAddIndicatorResponseToBlockMetadata(raw);
}
